import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import StatusBadge from "@/components/base/StatusBadge";
import { useSeo } from "@/hooks/useSeo";
import { events } from "@/mocks/events";
import TimelineNode from "./components/TimelineNode";

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const event = events.find((item) => item.id === id);

  const related = useMemo(
    () => (event ? events.filter((item) => item.category === event.category && item.id !== event.id) : []),
    [event],
  );

  const seo = useMemo(() => {
    if (!event) {
      return {
        title: "事件未找到 - 网报吃瓜",
        description: "抱歉，未找到该事件的时间线记录，可返回事件追踪列表重新查看。",
        jsonLd: undefined as Record<string, unknown> | undefined,
      };
    }
    const desc = `${event.summary}（网报吃瓜已整理 ${event.timeline.length} 条时间线节点，更新于 ${event.updatedAt}）`;
    return {
      title: `${event.title} - 网报吃瓜官方通报时间线`,
      description: desc,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Article",
            headline: event.title,
            description: event.summary,
            articleSection: event.category,
            datePublished: event.publishedAt,
            dateModified: event.updatedAt,
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "网报吃瓜" },
            publisher: { "@type": "Organization", name: "网报吃瓜" },
            mainEntityOfPage: `https://sxdayun.com.cn/events/${event.id}`,
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://sxdayun.com.cn/" },
              { "@type": "ListItem", position: 2, name: "事件追踪", item: "https://sxdayun.com.cn/events" },
              {
                "@type": "ListItem",
                position: 3,
                name: event.title,
                item: `https://sxdayun.com.cn/events/${event.id}`,
              },
            ],
          },
        ],
      } as Record<string, unknown>,
    };
  }, [event]);

  useSeo({
    title: seo.title,
    description: seo.description,
    keywords: event
      ? `${event.tags.join(",")},网报吃瓜${event.category},${event.category}官方通报`
      : "网报吃瓜事件追踪",
    path: `/events/${id ?? ""}`,
    ogType: "article",
    ogImage: event?.cover,
    jsonLd: seo.jsonLd,
  });

  if (!event) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="flex max-w-md flex-col items-center text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-file-unknow-line text-2xl"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
              没有找到这条时间线
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              该事件可能已被合并到其他条目，或链接有误。可以回到事件追踪列表重新查找。
            </p>
            <Link
              to="/events"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-list-check-3"></i>
              </span>
              返回事件追踪
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "事件追踪", to: "/events" },
                { label: event.title },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Link
                to={`/events?category=${encodeURIComponent(event.category)}`}
                className="inline-flex items-center rounded-md bg-background-200/80 px-2.5 py-1 text-xs font-medium text-foreground-700 hover:text-primary-700"
              >
                {event.category}
              </Link>
              <StatusBadge status={event.status} />
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {event.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {event.summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                首次收录 {event.publishedAt}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-refresh-line"></i>
                </span>
                最近更新 {event.updatedAt}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-node-tree"></i>
                </span>
                共 {event.timeline.length} 条时间线节点
              </span>
            </div>
          </div>
        </section>

        <section className="w-full">
          <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-[1fr_340px] lg:gap-14">
            <article className="min-w-0">
              <div className="mb-8 h-56 w-full overflow-hidden rounded-lg bg-background-100 md:h-80">
                <img
                  src={event.cover}
                  alt={`${event.title} 相关配图`}
                  title={`${event.title} · 官方通报时间线`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-history-line"></i>
                </span>
                时间线还原
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                以下节点按发布时间先后排列，从最初的传言到官方回应、通报与后续进展，保留发布主体与来源类型，便于对照复盘。
              </p>

              <ol className="mt-8">
                {event.timeline.map((item, index) => (
                  <TimelineNode
                    key={`${item.time}-${index}`}
                    item={item}
                    isLast={index === event.timeline.length - 1}
                  />
                ))}
              </ol>

              <div className="mt-8 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-100/60 p-4">
                <span className="text-xs font-medium text-foreground-500">相关标签</span>
                {event.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/events?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100/80 px-3 py-1 text-xs text-secondary-900 hover:bg-secondary-200"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </article>

            <aside className="lg:sticky lg:top-24 lg:h-fit">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">阅读提示</h2>
                <ul className="mt-3 flex flex-col gap-3 text-xs leading-relaxed text-foreground-600">
                  <li className="flex gap-2">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                      <i className="ri-check-line"></i>
                    </span>
                    「通报」「辟谣」以官方公开发布原文为准，「传言」不代表事实成立。
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                      <i className="ri-check-line"></i>
                    </span>
                    标记为「进展」的内容为阶段性说明，最终结论以官方通报为准。
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                      <i className="ri-check-line"></i>
                    </span>
                    转发前建议先看最新一条节点，避免使用已经过时的中间结论。
                  </li>
                </ul>
              </div>

              {related.length > 0 && (
                <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">
                    同类事件推荐
                  </h2>
                  <ul className="mt-4 flex flex-col gap-4">
                    {related.slice(0, 4).map((item) => (
                      <li key={item.id}>
                        <Link to={`/events/${item.id}`} className="group block">
                          <h3 className="text-sm font-medium leading-snug text-foreground-800 group-hover:text-accent-600">
                            {item.title}
                          </h3>
                          <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
                            <StatusBadge status={item.status} />
                            <span>更新 {item.updatedAt}</span>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/events?category=${encodeURIComponent(event.category)}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-600"
                  >
                    查看更多{event.category}事件
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                </div>
              )}

              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100/60 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">
                  发现遗漏的官方发声？
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  把你看到的通报原文链接与发布日期提交过来，核实后会补充到这条时间线。
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-semibold text-background-50 hover:bg-accent-600"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-send-plane-line"></i>
                  </span>
                  提交线索
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}